import { FlexAxisType } from 'common/pill-v2/pill-positioning.js';
import { CSSProperties } from 'react';
import { assertUnreachable } from 'utilities/buildTables/types';
import { messageLog } from 'utilities/helpers/logger/message-logger';

const PILL_POINTER_BASE = 16;
const PILL_POINTER_LENGTH = 8;
const PILL_POINTER_OFFSET_X = 6;
const PILL_POINTER_OFFSET_Y = PILL_POINTER_LENGTH;

export const getPillPointerTranslate = ({
  isDot,
  hasPoint,
  labelPos,
  pointOffsetX,
  pointOffsetY,
}: {
  isDot?: boolean;
  hasPoint?: boolean;
  labelPos?: FlexAxisType;
  pointOffsetX?: number;
  pointOffsetY?: number;
}): Partial<Record<'top' | 'bottom' | 'left' | 'right', CSSProperties['top']>> => {
  if (hasPoint && !isDot) {
    switch (labelPos) {
      case 'right':
        return { top: pointOffsetY, left: `${PILL_POINTER_OFFSET_X + (pointOffsetX ?? 0)}px` };
      case 'top':
        return { left: pointOffsetX, bottom: `${PILL_POINTER_OFFSET_Y + (pointOffsetY ?? 0)}px` };
      case 'bottom':
        return { left: pointOffsetX, top: `${PILL_POINTER_OFFSET_Y - (pointOffsetY ?? 0)}px` };
      case 'left':
        return { top: pointOffsetY, right: `${PILL_POINTER_OFFSET_X - (pointOffsetX ?? 0)}px` };
    }
  }
  return {
    top: pointOffsetY,
    left: pointOffsetX,
  };
};

/** Adds pill pointer is required
 
 * used for centering because it's based on the element's own dimensions
 * transform: translate(-50%, 0)  /* Moves left by half width, no vertical change
 * transform: translate(0, -50%)  /* No horizontal change, moves up by half height
 * transform: translate(-50%, -50%) /* Centers both horizontally and vertically
 */
export const PillPointer = ({
  border,
  hasPoint,
  labelPos,
  id,
}: {
  border?: string;
  hasPoint: boolean | undefined;
  labelPos?: FlexAxisType;
  id?: string;
}) => {
  if (hasPoint) {
    if (labelPos) {
      switch (labelPos) {
        case 'right':
          return (
            <div
              className="u-posAbs pill--point"
              style={{
                height: `${PILL_POINTER_BASE}px`,
                width: `${PILL_POINTER_LENGTH}px`,
                left: `-${PILL_POINTER_OFFSET_X - 1}px`,

                /** Center vertically */
                top: '50%',
                transform: 'translate(0, -50%)',

                background: border,
                clipPath: 'polygon(0 50%, 100% 0, 100% 100%)', // Triangle pointing left
              }}
            />
          );
        case 'top':
          return (
            <div
              className="u-posAbs pill--point"
              style={{
                height: `${PILL_POINTER_LENGTH}px`,
                width: `${PILL_POINTER_BASE}px`,
                bottom: `-${PILL_POINTER_OFFSET_Y - 1}px`,

                /** Center horizontally */
                left: '50%',
                transform: 'translate(-50%, 0)',

                background: border,
                clipPath: 'polygon(0 0, 100% 0, 50% 100%)', // Triangle pointing down
              }}
            />
          );
        case 'bottom':
          return (
            <div
              className="u-posAbs pill--point"
              style={{
                height: `${PILL_POINTER_LENGTH}px`,
                width: `${PILL_POINTER_BASE}px`,
                top: `-${PILL_POINTER_OFFSET_Y - 1}px`,

                /** Center horizontally */
                left: '50%',
                transform: 'translate(-50%, 0)',

                background: border,
                clipPath: 'polygon(50% 0, 0 100%, 100% 100%)', // Triangle pointing up
              }}
            />
          );
        case 'left':
          return (
            <div
              className="u-posAbs pill--point"
              style={{
                height: `${PILL_POINTER_BASE}px`,
                width: `${PILL_POINTER_LENGTH}px`,
                right: `-${PILL_POINTER_OFFSET_X - 1}px`,

                /** Center vertically */
                top: '50%',
                transform: 'translate(0, -50%)',

                background: border,
                clipPath: 'polygon(0 0, 0 100%, 100% 50%)', // Triangle pointing right
              }}
            />
          );

        case 'centre':
        case 'above':
        case 'below':
          return null;

        default:
          assertUnreachable(labelPos);
      }
    }
    messageLog.error(`M2F: Marker ${id} has point but no labelPos`);
  }
  return null;
};
